/* gara-collection-cards.js — THE one grid-completion loader (the 250-cap fix,
 * design/collection_grid_scale/RESEARCH_250cap_2026-08-26.md).
 *
 * Shopify clamps {% paginate %} at 250/page, so on a >250 collection the
 * client-side bike filter only sees page 1 unless the grid is completed. Three
 * loaders used to co-fire on one grid (inline stopgap + gara-grid-complete.js +
 * the bike-finder's loadAllPagesThenFilter — the 697-duplicate-cards incident
 * class). This file replaces all three with ONE owner:
 *
 *   no bike set            -> DO NOTHING (native pagination stands, zero fetches)
 *   bike + shard present   -> render ONLY the fitting cards from the
 *                             window.GARA_CARDS_<handle> lean-card shard
 *                             (one CDN-cached asset; ~5-22 cards vs 1,696 DOM
 *                             nodes), then live price/availability top-up via
 *                             one tokenless Storefront GraphQL call.
 *   bike + no shard        -> sequential, deduped ?page=N fetch of the
 *                             collection's own pages (the hardened stopgap,
 *                             kept as the safety net for a collection that
 *                             crosses 250 before the next shard build).
 *   native filter / sort   -> the shard is whole-collection + default-order,
 *                             so filtered/sorted views use the page-fetch lane
 *                             (it preserves the query params = the same view).
 *   tire grids             -> untouched; gara-tire-finder.js owns them.
 *
 * The card renderer emits markup matching snippets/gara-product-card.liquid
 * byte-for-byte in the load-bearing attributes (classes, data-sku, discipline,
 * tire grouper fields), so the discipline CSS, the YMM filter, the grouper and
 * the count script keep working untouched. Parity is asserted at build time by
 * canonical/card_shards.py --verify.
 *
 * Public API: window.GaraCardShard.sync() -> Promise (resolves when the grid is
 * complete for the current bike). gara-bike-finder.liquid awaits it before
 * running its filter pass.
 */
(function () {
  'use strict';

  var MAX_PAGES = 80;           // 3,754-product hub at 48/page = 79 pages (fallback lane only)
  var inflight = null;          // single in-flight sync across callers
  var pagesDone = false;        // page-fetch lane completed once already
  var shardDone = false;        // shard lane rendered for the current bike key
  var shardBikeKey = null;

  function grid() { return document.getElementById('gara-product-grid'); }

  function bike() {
    try { return (window.GaraYMM && window.GaraYMM.get) ? window.GaraYMM.get() : null; }
    catch (e) { return null; }
  }

  function isTireGrid(g) {
    // .gtf is the TIRE finder root (gara-tire-finder.liquid) — its own loader
    // owns those grids (phase 4). ONLY the finder marks a tire grid: keying on
    // a stray .gara-pcard-tire card would turn one tire product in a parts
    // collection into a silent dead end (no loader at all). The tire-card
    // GROUPER is global and handles family cards wherever they render.
    return !!document.querySelector('.gtf');
  }

  function viewIsFiltered() {
    // Native filter / tag / sort views are a DIFFERENT product set or order than
    // the shard (whole collection, default sort) — page-fetch lane handles them.
    try {
      var u = new URL(window.location.href);
      if (u.searchParams.get('sort_by')) return true;
      var keys = [];
      u.searchParams.forEach(function (v, k) { keys.push(k); });
      if (keys.some(function (k) { return k.indexOf('filter.') === 0 || k === 'q' || k === 'constraint'; })) return true;
      // tag-filtered path: /collections/<handle>/<tag>
      var m = u.pathname.match(/^\/collections\/[^/]+\/(.+)$/);
      if (m && m[1].indexOf('products/') !== 0) return true;
    } catch (e) {}
    return false;
  }

  function collectionHandle() {
    var m = window.location.pathname.match(/^\/collections\/([^/]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  function shardFor(handle) {
    if (!handle) return null;
    return window['GARA_CARDS_' + handle.replace(/[^A-Za-z0-9_]/g, '_')] || null;
  }

  function cardHandle(card) {
    var href = card.getAttribute && card.getAttribute('href');
    var m = href && href.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function domHandles(g) {
    var seen = {};
    g.querySelectorAll('.gara-pcard').forEach(function (c) {
      var h = cardHandle(c);
      if (h) seen[h] = true;
    });
    return seen;
  }

  function urlPrefix(g) {
    // Mirror `product.url | within: collection`: reuse the SSR cards' own href
    // prefix so injected URLs are identical in shape.
    var first = g.querySelector('.gara-pcard[href*="/products/"]');
    if (first) {
      var href = first.getAttribute('href');
      return href.slice(0, href.indexOf('/products/'));
    }
    var h = collectionHandle();
    return h ? '/collections/' + h : '';
  }

  function classifySku(sku, ymm) {
    // The EXACT classification the grid filter uses (GaraYMM.classifyViaIndex
    // reads data-sku off a card): feed it a stub card so shard SKUs and DOM
    // cards can never be judged by different rules.
    if (!window.GaraYMM || !window.GaraYMM.classifyViaIndex) return null;
    var stub = { getAttribute: function (n) { return n === 'data-sku' ? sku : null; } };
    return window.GaraYMM.classifyViaIndex(stub, ymm);
  }

  function money(cents) {
    var s = (cents / 100).toFixed(2);
    var parts = s.split('.');
    return '$' + parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function imgUrl(enc, imgbase, size) {
    // enc = "name.ext|ver" under imgbase, or "!<full-url>" passthrough.
    if (!enc) return '';
    if (enc.charAt(0) === '!') {
      var full = enc.slice(1);
      return full.replace(/(\.[a-z0-9]+)(\?|$)/i, '_' + size + '$1$2');
    }
    var bar = enc.lastIndexOf('|');
    var name = bar >= 0 ? enc.slice(0, bar) : enc;
    var ver = bar >= 0 ? enc.slice(bar + 1) : '';
    var dot = name.lastIndexOf('.');
    var base = dot >= 0 ? name.slice(0, dot) : name;
    var ext = dot >= 0 ? name.slice(dot) : '';
    return imgbase + base + '_' + size + ext + (ver ? '?v=' + ver : '');
  }

  function buildCard(rec, imgbase, prefix) {
    // Markup mirror of snippets/gara-product-card.liquid (load-bearing parts).
    var flags = rec.b || '';
    var oos = flags.indexOf('o') >= 0;
    var noimg = flags.indexOf('n') >= 0 || !rec.i;
    var cls = 'gara-pcard';
    if (oos) cls += ' oos';
    if (noimg) cls += ' noimg';
    if (rec.z === 'acc') cls += ' gara-pcard-accessory';
    else if (rec.z) cls += ' ' + rec.z;
    if (rec.tf) cls += ' gara-pcard-tire';

    var a = document.createElement('a');
    a.href = prefix + '/products/' + rec.h;
    a.className = cls;
    a.setAttribute('data-sku', rec.k || '');
    a.setAttribute('data-fitment', '');
    a.setAttribute('data-discipline', rec.d || '');
    a.setAttribute('data-handle', rec.h);         // exact key for the freshness patch
    a.setAttribute('data-gara-injected', 'shard');
    if (flags.indexOf('e') >= 0) a.setAttribute('data-engine-oil', '1');
    if (rec.tf) {
      a.setAttribute('data-tire-family', rec.tf);
      a.setAttribute('data-tire-size', rec.ts || '');
      a.setAttribute('data-tire-position', rec.tp || '');
      a.setAttribute('data-product-url', prefix + '/products/' + rec.h);
      if (rec.vid) a.setAttribute('data-variant-id', rec.vid);
      a.setAttribute('data-price', String(rec.p));
    }
    a.setAttribute('aria-label', rec.t || '');

    var imgHtml;
    if (!noimg) {
      imgHtml =
        '<img class="gara-pcard-img-primary" src="' + esc(imgUrl(rec.i, imgbase, '600x600')) + '"' +
        ' srcset="' + esc(imgUrl(rec.i, imgbase, '400x400')) + ' 400w, ' +
        esc(imgUrl(rec.i, imgbase, '600x600')) + ' 600w, ' +
        esc(imgUrl(rec.i, imgbase, '900x900')) + ' 900w"' +
        ' sizes="(min-width: 1200px) 24vw, (min-width: 768px) 33vw, 50vw"' +
        ' alt="' + esc(rec.t) + '" loading="lazy">';
      /* hover image-swap removed 2026-08-28 (Benjamin ruling); rec.j stays in
         the shard data unused — no shard rebuild needed. */
    } else {
      imgHtml = '<div class="gara-pcard-noimg">No image</div>';
    }
    if (rec.q && rec.q > rec.p) imgHtml += '<div class="gara-pcard-badge sale">Sale</div>';
    if (oos) imgHtml += '<div class="gara-pcard-badge oos">Sold Out</div>';
    if (flags.indexOf('r') >= 0) imgHtml += '<div class="gara-pcard-badge race-only">Race Only</div>';

    var priceHtml;
    if (rec.pr) {
      priceHtml = '<span class="gara-pcard-price">' + money(rec.pr[0]) + ' &ndash; ' + money(rec.pr[1]) + '</span>';
    } else {
      priceHtml = '<span class="gara-pcard-price">' +
        ((rec.tf || rec.cf) ? '<span class="gara-pcard-tire-from">From </span>' : '') +
        money(rec.p) + '</span>';
    }
    if (rec.c) {
      priceHtml += '<span class="gara-pcard-colors" style="font-size:.72rem;color:#8a8578;white-space:nowrap;">' + rec.c + ' colors</span>';
    }

    var titleHtml = rec.tf
      ? '<h3 class="gara-pcard-title gara-pcard-tire-title">' + esc(rec.tf) + '</h3>' +
        '<div class="gara-pcard-tire-picker" data-tire-picker hidden></div>'
      : '<h3 class="gara-pcard-title">' + esc(rec.t) + '</h3>';

    a.innerHTML =
      '<div class="gara-pcard-img">' + imgHtml + '</div>' +
      '<div class="gara-pcard-meta">' +
      '<div class="gara-pcard-vendor">' + esc(rec.v) + '</div>' +
      titleHtml +
      '<div class="gara-pcard-price-row">' + priceHtml + '</div>' +
      '</div>';
    return a;
  }

  // ── fitment slice (2026-09-18 speed lever 2) ─────────────────────────────
  // The shard carries the product index's fitment verdict for its own SKUs
  // (`fits` table + per-card `f` ref). Register them as window.GARA_INDEX_SLICE
  // NOW, at script load: the shard tag precedes this file (both defer, document
  // order) and the finder boots at DOMContentLoaded, after every deferred
  // script. GaraYMM.classifyViaIndex consults the slice when the full index has
  // not landed yet (it is async on sharded collections), so the grid collapses
  // to the fitting cards without waiting on 7.4 MB of JS. The slice is a
  // separate global on purpose: gara-recs.js and the finder's store-wide count
  // key on GARA_PRODUCT_INDEX meaning "the whole catalog", and must keep doing so.
  function registerSlice() {
    var shard = shardFor(collectionHandle());
    if (!shard || !shard.fits || !shard.cards) return 0;
    var S = window.GARA_INDEX_SLICE = window.GARA_INDEX_SLICE || {};
    var n = 0;
    for (var i = 0; i < shard.cards.length; i++) {
      var rec = shard.cards[i];
      if (!rec.k || rec.f == null || !shard.fits[rec.f] || S[rec.k]) continue;
      S[rec.k] = { f: shard.fits[rec.f] };
      n++;
    }
    return n;
  }
  try { var _sliced = registerSlice(); if (_sliced) performance.mark('gara:slice-registered'); } catch (e) {}

  // ── freshness top-up (tokenless Storefront GraphQL, §4.4) ────────────────
  function freshnessTopUp(g, handles) {
    if (!handles.length) return;
    var q = 'query {';
    handles.slice(0, 60).forEach(function (h, i) {
      q += ' p' + i + ': product(handle: ' + JSON.stringify(h) + ') { handle availableForSale' +
        ' priceRange { minVariantPrice { amount } } } ';
    });
    q += '}';
    fetch('/api/2026-07/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: q })
    }).then(function (r) { return r.ok ? r.json() : null; })
      .then(function (body) {
        var data = body && body.data;
        if (!data) return;
        var byHandle = {};
        g.querySelectorAll('.gara-pcard[data-gara-injected="shard"]').forEach(function (c) {
          var h = c.getAttribute('data-handle');
          if (h && !byHandle[h]) byHandle[h] = c;
        });
        Object.keys(data).forEach(function (k) {
          var p = data[k];
          if (!p || !p.handle) return;
          // exact handle key — an href*= substring match patched a prefix-
          // extended sibling's card with another product's price (review catch)
          var card = byHandle[p.handle];
          if (!card) return;
          // availability: shard said in-stock but live says sold out (or back)
          var oosNow = p.availableForSale === false;
          var wasOos = card.classList.contains('oos');
          if (oosNow !== wasOos) {
            card.classList.toggle('oos', oosNow);
            var badge = card.querySelector('.gara-pcard-badge.oos');
            if (oosNow && !badge) {
              var b = document.createElement('div');
              b.className = 'gara-pcard-badge oos';
              b.textContent = 'Sold Out';
              var wrap = card.querySelector('.gara-pcard-img');
              if (wrap) wrap.appendChild(b);
            } else if (!oosNow && badge) badge.remove();
          }
          // price: patch only the simple single-price form (ranges/from keep build price)
          var amt = p.priceRange && p.priceRange.minVariantPrice && p.priceRange.minVariantPrice.amount;
          var el = card.querySelector('.gara-pcard-price');
          if (amt && el && !el.querySelector('.gara-pcard-tire-from') && el.textContent.indexOf('–') < 0) {
            var cents = Math.round(parseFloat(amt) * 100);
            if (isFinite(cents) && cents > 0) el.textContent = money(cents);
          }
        });
      })
      .catch(function () { /* cosmetic only — cart/checkout re-validate */ });
  }

  // ── shard lane ───────────────────────────────────────────────────────────
  function syncFromShard(g, shard, ymm) {
    var key = JSON.stringify([ymm.slug || '', ymm.make, ymm.model, ymm.year]);
    if (shardDone && shardBikeKey === key) return Promise.resolve();
    shardDone = true;
    shardBikeKey = key;
    var seen = domHandles(g);
    var prefix = urlPrefix(g);
    var added = [];
    var frag = document.createDocumentFragment();
    for (var i = 0; i < shard.cards.length; i++) {
      var rec = shard.cards[i];
      if (seen[rec.h]) continue;
      var verdict = classifySku(rec.k, ymm);
      if (verdict !== 'match' && verdict !== 'universal') continue;
      seen[rec.h] = true;
      frag.appendChild(buildCard(rec, shard.imgbase, prefix));
      added.push(rec.h);
    }
    if (added.length) {
      g.appendChild(frag);
      freshnessTopUp(g, added);
    }
    try { console.info('[gara-cards] shard rendered ' + added.length + ' fitting cards (of ' + shard.cards.length + ')'); } catch (e) {}
    return Promise.resolve();
  }

  // ── page-fetch fallback lane (the hardened stopgap, sequential + deduped) ─
  function syncFromPages(g) {
    if (pagesDone) return Promise.resolve();
    var total = parseInt(g.getAttribute('data-gara-coll-total') || '0', 10);
    // 2026-09-18: page 1 is 48 cards (was Shopify's 250 clamp); the section
    // advertises its page size so this lane never assumes one.
    var pageSize = parseInt(g.getAttribute('data-gara-page-size') || '250', 10) || 250;
    if (!total || g.querySelectorAll('.gara-pcard').length >= total) {
      pagesDone = true;
      return Promise.resolve();
    }
    pagesDone = true;
    var pages = Math.min(MAX_PAGES, Math.ceil(total / pageSize));
    var cur = 1;
    try { cur = parseInt(new URL(window.location.href).searchParams.get('page') || '1', 10) || 1; } catch (e) {}
    var chain = Promise.resolve();
    var loop = function (p) {
      chain = chain.then(function () {
        var url;
        try {
          var u = new URL(window.location.href);
          u.searchParams.set('page', p);
          url = u.pathname + '?' + u.searchParams.toString();
        } catch (e) { url = window.location.pathname + '?page=' + p; }
        return fetch(url, { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (html) {
            if (!html) return;
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var remote = doc.getElementById('gara-product-grid');
            if (!remote) return;
            var seen = domHandles(g);   // re-read live grid: exact dedupe
            remote.querySelectorAll('.gara-pcard').forEach(function (card) {
              var h = cardHandle(card);
              if (!h || seen[h]) return;
              seen[h] = true;
              card.setAttribute('data-gara-injected', 'page');
              card.style.display = 'none';        // hidden until the filter judges it
              g.appendChild(card);
            });
          })
          .catch(function () { /* a missed page under-completes, never breaks */ });
      });
    };
    for (var p = 1; p <= pages; p++) { if (p !== cur) loop(p); }
    return chain;
  }

  // ── public sync ──────────────────────────────────────────────────────────
  function sync() {
    if (inflight) return inflight;
    var g = grid();
    var done = function () { inflight = null; };
    if (!g || isTireGrid(g)) return Promise.resolve();
    var ymm = bike();
    if (!ymm || !ymm.make) return Promise.resolve();   // no bike: never fetch anything
    var shard = viewIsFiltered() ? null : shardFor(collectionHandle());
    var usedPages = !shard;
    var run = shard ? syncFromShard(g, shard, ymm) : syncFromPages(g);
    inflight = run.then(function () {
      done();
      try { performance.mark('gara:grid-synced'); } catch (e) {}
      // The page lane appends cards hidden (display:none) — sync() itself must
      // judge them, because not every caller reaches a filter pass afterwards
      // (the bike-finder's applyFilter only toggles the .gbf-hidden CLASS; a
      // class removal cannot clear an inline style). This mirrors the
      // refilter() pairing the legacy grid-complete loader had — dropping it
      // left every matching page-2+ card permanently invisible (review catch:
      // the exact MT-09-SP symptom class this system exists to fix).
      try {
        if (usedPages && window.GaraYMM && window.GaraYMM.filterGrid) {
          window.GaraYMM.filterGrid();
        }
      } catch (e) {}
      try { document.dispatchEvent(new CustomEvent('gara-grid:append')); } catch (e) {}
      try { document.dispatchEvent(new CustomEvent('gara-grid:refilled')); } catch (e) {}
      try { document.dispatchEvent(new CustomEvent('gara-discipline:change')); } catch (e) {}
    }, done);
    return inflight;
  }

  // buildCard/imgUrl exposed for the /pages/your-bike grid (S163-B): ONE card
  // renderer site-wide, so shard-injected and your-bike cards can never drift.
  window.GaraCardShard = { sync: sync, buildCard: buildCard, imgUrl: imgUrl };

  // Bike set/changed by ANY surface (mega-nav MY BIKE, another widget): complete
  // the grid, then re-run the filter + counts. The bike-finder's own flow calls
  // sync() explicitly before its filter pass; the in-flight guard dedupes.
  document.addEventListener('gara-ymm:change', function (e) {
    if (!e.detail) {
      // Bike CLEARED. Shard-injected cards are a bike-specific subset — remove
      // them (page-fetch-lane cards are the real full collection and stay).
      // Then restore the grid OURSELVES: the finder's clearFilter only runs
      // from its own reset button, so a clear from any other surface (mega-nav
      // MY BIKE) never reaches it (review catch — the 2026-08-16 near-empty-
      // grid class). Mirrors clearFilter: strip .gbf-hidden + inline
      // order/display, drop the universal divider, restore pagination, hide
      // the finder's empty state.
      var g = grid();
      if (g) g.querySelectorAll('.gara-pcard[data-gara-injected="shard"]').forEach(function (c) { c.remove(); });
      shardDone = false;
      shardBikeKey = null;
      document.querySelectorAll('.gbf-hidden').forEach(function (el) { el.classList.remove('gbf-hidden'); });
      document.querySelectorAll('.gara-pcard').forEach(function (c) {
        c.style.order = '';
        c.style.display = '';
        c.classList.remove('gara-ymm-no-match', 'gara-ymm-no-data');
      });
      var d = document.getElementById('gbf-uni-divider');
      if (d && d.parentNode) d.parentNode.removeChild(d);
      document.querySelectorAll('.gara-pagination, nav[aria-label="Pagination"]').forEach(function (el) {
        el.style.display = '';
      });
      var eb = document.getElementById('gbf-empty');
      if (eb) eb.style.display = 'none';
      try { document.dispatchEvent(new CustomEvent('gara-discipline:change')); } catch (err) {}
      return;
    }
    // Bike SET or CHANGED by any surface. Strip the finder's .gbf-hidden class
    // first: filterGrid hides via inline display only, so a card class-hidden
    // for bike A would stay invisible for bike B even when it matches (the
    // class carries display:none !important — review catch).
    document.querySelectorAll('.gbf-hidden').forEach(function (el) { el.classList.remove('gbf-hidden'); });
    sync().then(function () {
      try {
        if (window.GaraYMM && window.GaraYMM.filterGrid) window.GaraYMM.filterGrid();
        document.dispatchEvent(new CustomEvent('gara-discipline:change'));
      } catch (err) {}
    });
  });
})();
